import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, ClipboardList, ChevronRight, Clock, CheckCircle2, Truck, Factory, XCircle } from "lucide-react";
import { formatDate } from "../lib/format";
import UserBadge from "../components/UserBadge";
import DateRangeFilter, { rangeFromPreset, filterOrdersByRange } from "../components/DateRangeFilter";

const STATUS_META = {
  pending:       { label: "Pending",       color: "bg-yellow-50 text-yellow-700",  icon: Clock },
  in_production: { label: "In Production", color: "bg-purple-50 text-purple-700",  icon: Factory },
  dispatched:    { label: "Dispatched",    color: "bg-orange-50 text-orange-700",  icon: Truck },
  completed:     { label: "Completed",     color: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  cancelled:     { label: "Cancelled",     color: "bg-red-50 text-red-700",         icon: XCircle },
};

const STATUS_TABS = [
  { key: "all",           label: "All" },
  { key: "pending",       label: "Pending" },
  { key: "in_production", label: "In Production" },
  { key: "dispatched",    label: "Dispatched" },
  { key: "completed",     label: "Completed" },
];

const emptyItem = () => ({ brand: "", size: "", quantity: 1, unit: "cartons" });

export default function Orders() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStatus = searchParams.get("status") || "all";
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    party_id: "", employee_id: "", order_date: "", dispatch_by: "", notes: "", items: [emptyItem()],
  });
  const [range, setRange] = useState({ key: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    const [o, p, e, pr] = await Promise.all([
      api.get("/orders"), api.get("/parties"), api.get("/employees"), api.get("/products"),
    ]);
    setRows(o.data); setParties(p.data); setEmployees(e.data); setProducts(pr.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateItem = (idx, field, value) => {
    setForm((f) => {
      const items = f.items.map((it, i) => i === idx ? { ...it, [field]: value } : it);
      return { ...f, items };
    });
  };
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeRow = (idx) => setForm((f) => ({
    ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = form.items
      .filter((it) => it.brand.trim() && it.size.trim() && parseInt(it.quantity, 10) > 0)
      .map((it) => ({ brand: it.brand.trim(), size: it.size.trim(), quantity: parseInt(it.quantity, 10), unit: it.unit }));
    if (cleaned.length === 0) { toast.error("Add at least one line item"); return; }
    try {
      await api.post("/orders", {
        party_id: form.party_id,
        employee_id: form.employee_id || null,
        items: cleaned,
        order_date: form.order_date || null,
        dispatch_by: form.dispatch_by,
        notes: form.notes,
      });
      toast.success("Order created");
      setOpen(false);
      setForm({ party_id: "", employee_id: "", order_date: "", dispatch_by: "", notes: "", items: [emptyItem()] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Move this order to the Recycle Bin? You can restore within 45 days.")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Moved to Recycle Bin");
    load();
  };

  // Apply date-range first, then status tab.
  const rangeDates = rangeFromPreset(range.key, range.customFrom, range.customTo);
  const rangeFiltered = useMemo(
    () => filterOrdersByRange(rows, rangeDates.from, rangeDates.to),
    [rows, rangeDates.from, rangeDates.to]
  );

  const counts = useMemo(() => {
    const c = { all: rangeFiltered.length, pending: 0, in_production: 0, dispatched: 0, completed: 0 };
    for (const r of rangeFiltered) if (c[r.status] !== undefined) c[r.status] += 1;
    return c;
  }, [rangeFiltered]);

  const filtered = activeStatus === "all" ? rangeFiltered : rangeFiltered.filter((r) => r.status === activeStatus);

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Orders & Dispatch"
        subtitle="Track each order from Pending → In Production → Dispatched → Completed"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <DateRangeFilter value={range} onChange={setRange} testidPrefix="orders-range" />
            <button
              onClick={() => {
                if (parties.length === 0) return toast.error("Add a buyer first");
                setOpen(true);
              }}
              data-testid="add-order-btn"
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="h-5 w-5" /> New Order
            </button>
          </div>
        }
      />

      {/* Status tabs */}
      <div className="card-surface p-2 mb-6 flex flex-wrap gap-1">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSearchParams(t.key === "all" ? {} : { status: t.key })}
            data-testid={`orders-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-figtree font-medium transition-colors duration-200 ${
              activeStatus === t.key
                ? "bg-forest text-white"
                : "text-ink-soft hover:bg-sand"
            }`}
          >
            {t.label} <span className="opacity-70">({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-forest-soft text-forest grid place-items-center mb-3">
              <ClipboardList className="h-6 w-6" />
            </div>
            <div className="font-outfit font-semibold text-ink">No orders in this view</div>
            <div className="text-ink-soft text-sm font-figtree">Create a new order or switch tabs above.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Status</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Received</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Buyer</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Items</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Dispatch By</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Assigned</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">By</th>
                  <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  const overdue = o.dispatch_by && !["completed", "cancelled", "dispatched"].includes(o.status)
                    && new Date(o.dispatch_by) < new Date(new Date().toDateString());
                  const items = o.items || [];
                  const totalUnits = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
                  const uniqueBrands = [...new Set(items.map((it) => it.brand))];
                  return (
                    <tr
                      key={o.id}
                      onClick={() => nav(`/orders/${o.id}`)}
                      data-testid={`order-row-${o.id}`}
                      className="border-t border-line hover:bg-bone/60 cursor-pointer align-middle"
                    >
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-figtree font-semibold ${meta.color}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm whitespace-nowrap">{formatDate(o.received_at)}</td>
                      <td className="px-6 py-4 font-figtree font-medium text-ink">{o.party_name}</td>
                      <td className="px-6 py-4 font-figtree text-ink">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-outfit font-semibold text-ink">
                            {items.length} item{items.length !== 1 ? "s" : ""}
                          </span>
                          <span className="text-xs text-ink-soft">·</span>
                          <span className="text-sm text-ink-soft">{totalUnits} units</span>
                        </div>
                        {uniqueBrands.length > 0 && (
                          <div className="text-xs text-ink-mute mt-0.5 truncate max-w-[240px]">
                            {uniqueBrands.slice(0, 2).join(", ")}
                            {uniqueBrands.length > 2 && ` +${uniqueBrands.length - 2}`}
                          </div>
                        )}
                      </td>
                      <td className={`px-6 py-4 font-figtree text-sm whitespace-nowrap ${overdue ? "text-red-700 font-semibold" : "text-ink-soft"}`}>
                        {o.dispatch_by ? formatDate(o.dispatch_by) : "—"}
                        {overdue && <div className="text-xs">overdue</div>}
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm">{o.employee_name || "—"}</td>
                      <td className="px-6 py-4">
                        <UserBadge user={o.last_edited_by || o.created_by} testid={`order-by-${o.id}`} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end items-center gap-1">
                          <button onClick={(e) => del(o.id, e)} data-testid={`order-delete-${o.id}`} className="p-2 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200">
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <ChevronRight className="h-4 w-4 text-ink-mute" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0">
            <DialogTitle className="font-outfit">New Order</DialogTitle>
          </DialogHeader>
          <datalist id="order-product-list">
            {products.map((p) => <option key={p.id} value={p.name} />)}
          </datalist>
          <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5" data-testid="order-form-scroll">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-figtree font-medium text-ink block mb-2">Employee (pick first)</label>
                <select
                  data-testid="order-employee-select"
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value, party_id: "" })}
                  className="w-full h-12 px-4 rounded-lg border border-line bg-white outline-none focus:ring-2 focus:ring-forest"
                >
                  <option value="">None (show all buyers)</option>
                  {employees.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-figtree font-medium text-ink block mb-2">
                  Buyer
                  {form.employee_id && <span className="text-xs text-ink-soft font-normal ml-2">(filtered)</span>}
                </label>
                <select
                  data-testid="order-party-select"
                  required value={form.party_id}
                  onChange={(e) => setForm({ ...form, party_id: e.target.value })}
                  className="w-full h-12 px-4 rounded-lg border border-line bg-white outline-none focus:ring-2 focus:ring-forest"
                >
                  <option value="">Select buyer</option>
                  {parties
                    .filter((p) => !form.employee_id || p.employee_id === form.employee_id)
                    .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-figtree font-medium text-ink block mb-2">Order date</label>
                <input
                  type="date"
                  data-testid="order-date-input"
                  value={form.order_date}
                  onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                  className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
                />
                <div className="text-xs text-ink-mute font-figtree mt-1">Leave blank for today</div>
              </div>
              <div>
                <label className="text-sm font-figtree font-medium text-ink block mb-2">Dispatch by</label>
                <input
                  type="date"
                  data-testid="order-dispatch-input"
                  value={form.dispatch_by}
                  onChange={(e) => setForm({ ...form, dispatch_by: e.target.value })}
                  className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-figtree font-medium text-ink">Line Items</label>
                <button
                  type="button"
                  onClick={addRow}
                  data-testid="order-add-row-btn"
                  className="text-sm font-figtree font-semibold text-forest hover:text-forest-hover flex items-center gap-1 transition-colors duration-200"
                >
                  <Plus className="h-4 w-4" /> Add Row
                </button>
              </div>

              <div className="rounded-lg border border-line overflow-hidden">
                <div className="grid grid-cols-12 gap-2 bg-sand/60 px-3 py-2 text-xs font-figtree font-semibold text-ink-soft">
                  <div className="col-span-4">Brand</div>
                  <div className="col-span-2">Size</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-3">Unit</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="divide-y divide-line">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`order-item-row-${idx}`}>
                      <input data-testid={`order-item-brand-${idx}`} value={it.brand} onChange={(e) => updateItem(idx, "brand", e.target.value)} list="order-product-list" placeholder="Pick or type a brand" className="col-span-4 h-10 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm" />
                      <select data-testid={`order-item-size-${idx}`} value={it.size} onChange={(e) => updateItem(idx, "size", e.target.value)} className="col-span-2 h-10 px-2 rounded border border-line bg-white outline-none focus:ring-1 focus:ring-forest text-sm">
                        <option value="">Size</option>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="2XL">2XL</option>
                        <option value="3XL">3XL</option>
                        <option value="4XL">4XL</option>
                        <option value="5XL">5XL</option>
                      </select>
                      <input data-testid={`order-item-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="col-span-2 h-10 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm" />
                      <select data-testid={`order-item-unit-${idx}`} value={it.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} className="col-span-3 h-10 px-2 rounded border border-line bg-white outline-none focus:ring-1 focus:ring-forest text-sm">
                        <option value="cartons">cartons</option>
                        <option value="pacs">pacs</option>
                      </select>
                      <div className="col-span-1 flex items-center justify-end">
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => removeRow(idx)} data-testid={`order-item-remove-${idx}`} className="p-1 rounded text-ink-soft hover:bg-red-50 hover:text-red-700">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Notes</label>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            </div>

            <DialogFooter className="px-6 py-4 flex-shrink-0 border-t border-line bg-white">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-line font-figtree">Cancel</button>
              <button data-testid="order-submit-btn" type="submit" className="btn-primary">Save Order</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
